import { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Trash2, Circle, Square, ArrowRight, Minus, Type } from 'lucide-react';
import type { ImageAnnotation } from '@/hooks/useToothImages';

type AnnotationKind = ImageAnnotation['kind'];

const PRESET_COLORS = ['#ef4444', '#facc15', '#22c55e', '#3b82f6', '#ffffff'];

interface AnnotationToolProps {
  annotations: ImageAnnotation[];
  onChange: (annotations: ImageAnnotation[]) => void;
  imageWidth: number;
  imageHeight: number;
  active: boolean;
  onToggleActive: () => void;
  userId: string;
}

/**
 * SVG-based annotation tool with toolbar for kind + color selection.
 * Supports line, circle, rect, arrow, freehand, text annotations.
 */
export function AnnotationTool({
  annotations,
  onChange,
  imageWidth,
  imageHeight,
  active,
  onToggleActive,
  userId,
}: AnnotationToolProps) {
  const { t } = useTranslation();
  const svgRef = useRef<SVGSVGElement>(null);

  const [kind, setKind] = useState<AnnotationKind>('line');
  const [color, setColor] = useState('#ef4444');
  const [dragging, setDragging] = useState<[number, number] | null>(null);
  const [currentPoints, setCurrentPoints] = useState<number[]>([]);
  const [freehandPath, setFreehandPath] = useState<number[]>([]);

  const scaleX = imageWidth > 0 ? 100 / imageWidth : 1;
  const scaleY = imageHeight > 0 ? 100 / imageHeight : 1;

  const getSvgPoint = useCallback(
    (e: React.MouseEvent<SVGSVGElement>): [number, number] => {
      const rect = svgRef.current!.getBoundingClientRect();
      return [
        ((e.clientX - rect.left) / rect.width) * imageWidth,
        ((e.clientY - rect.top) / rect.height) * imageHeight,
      ];
    },
    [imageWidth, imageHeight],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      if (!active) return;
      const pt = getSvgPoint(e);
      setDragging(pt);
      if (kind === 'freehand') {
        setFreehandPath([...pt]);
      } else {
        setCurrentPoints([...pt, ...pt]);
      }
    },
    [active, getSvgPoint, kind],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      if (!active || !dragging) return;
      const pt = getSvgPoint(e);
      if (kind === 'freehand') {
        setFreehandPath((prev) => [...prev, ...pt]);
      } else {
        setCurrentPoints((prev) => [prev[0], prev[1], ...pt]);
      }
    },
    [active, dragging, getSvgPoint, kind],
  );

  const handleMouseUp = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      if (!active || !dragging) return;
      const pt = getSvgPoint(e);
      setDragging(null);

      let points: number[];
      if (kind === 'freehand') {
        points = [...freehandPath, ...pt];
        setFreehandPath([]);
      } else {
        points = [currentPoints[0], currentPoints[1], ...pt];
      }

      if (points.length < 4) return;
      setCurrentPoints([]);

      const ann: ImageAnnotation = {
        id: crypto.randomUUID(),
        kind,
        points,
        color,
        created_by: userId,
        created_at: new Date().toISOString(),
      };
      onChange([...annotations, ann]);
    },
    [active, dragging, getSvgPoint, kind, freehandPath, currentPoints, color, userId, annotations, onChange],
  );


  const renderAnnotation = (ann: ImageAnnotation, isPreview = false) => {
    const opacity = isPreview ? 0.7 : 1;
    const pts = ann.points.map((v, i) => v * (i % 2 === 0 ? scaleX : scaleY));

    if (ann.kind === 'line' && pts.length >= 4) {
      return (
        <line
          key={ann.id}
          x1={pts[0]}
          y1={pts[1]}
          x2={pts[2]}
          y2={pts[3]}
          stroke={ann.color}
          strokeWidth="0.5"
          opacity={opacity}
        />
      );
    }
    if (ann.kind === 'circle' && pts.length >= 4) {
      const cx = (pts[0] + pts[2]) / 2;
      const cy = (pts[1] + pts[3]) / 2;
      const rx = Math.abs(pts[2] - pts[0]) / 2;
      const ry = Math.abs(pts[3] - pts[1]) / 2;
      return (
        <ellipse
          key={ann.id}
          cx={cx}
          cy={cy}
          rx={rx}
          ry={ry}
          stroke={ann.color}
          strokeWidth="0.5"
          fill="none"
          opacity={opacity}
        />
      );
    }
    if (ann.kind === 'rect' && pts.length >= 4) {
      const x = Math.min(pts[0], pts[2]);
      const y = Math.min(pts[1], pts[3]);
      return (
        <rect
          key={ann.id}
          x={x}
          y={y}
          width={Math.abs(pts[2] - pts[0])}
          height={Math.abs(pts[3] - pts[1])}
          stroke={ann.color}
          strokeWidth="0.5"
          fill="none"
          opacity={opacity}
        />
      );
    }
    if (ann.kind === 'arrow' && pts.length >= 4) {
      const markerId = `arrowhead-${ann.id}`;
      return (
        <g key={ann.id} opacity={opacity}>
          <defs>
            <marker id={markerId} markerWidth="4" markerHeight="4" refX="3" refY="2" orient="auto">
              <path d="M0,0 L4,2 L0,4 Z" fill={ann.color} />
            </marker>
          </defs>
          <line
            x1={pts[0]}
            y1={pts[1]}
            x2={pts[2]}
            y2={pts[3]}
            stroke={ann.color}
            strokeWidth="0.5"
            markerEnd={`url(#${markerId})`}
          />
        </g>
      );
    }
    if (ann.kind === 'freehand' && pts.length >= 4) {
      const d = pts
        .reduce<string[]>((acc, v, i) => {
          if (i % 2 === 0) {
            const x = v;
            const y = pts[i + 1];
            acc.push(i === 0 ? `M${x},${y}` : `L${x},${y}`);
          }
          return acc;
        }, [])
        .join(' ');
      return (
        <path key={ann.id} d={d} stroke={ann.color} strokeWidth="0.5" fill="none" opacity={opacity} />
      );
    }
    return null;
  };

  const kindIcons: Record<AnnotationKind, React.ReactNode> = {
    line: <Minus className="h-4 w-4" />,
    circle: <Circle className="h-4 w-4" />,
    rect: <Square className="h-4 w-4" />,
    arrow: <ArrowRight className="h-4 w-4" />,
    freehand: <Pencil className="h-4 w-4" />,
    text: <Type className="h-4 w-4" />,
  };

  return (
    <>
      {/* SVG layer */}
      <svg
        ref={svgRef}
        className="absolute inset-0 h-full w-full"
        style={{ pointerEvents: active ? 'all' : 'none', cursor: active ? 'crosshair' : 'default' }}
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => {
          setDragging(null);
          setCurrentPoints([]);
          setFreehandPath([]);
        }}
      >
        {annotations.map((ann) => renderAnnotation(ann))}

        {/* Preview of current drag */}
        {dragging && currentPoints.length >= 4 &&
          renderAnnotation(
            {
              id: '__preview__',
              kind,
              points: currentPoints.map((v, i) =>
                v / (i % 2 === 0 ? scaleX : scaleY),
              ),
              color,
              created_by: userId,
              created_at: '',
            },
            true,
          )}
        {dragging && freehandPath.length >= 2 && kind === 'freehand' &&
          renderAnnotation(
            {
              id: '__freehand_preview__',
              kind: 'freehand',
              points: freehandPath.map((v, i) =>
                v / (i % 2 === 0 ? scaleX : scaleY),
              ),
              color,
              created_by: userId,
              created_at: '',
            },
            true,
          )}
      </svg>

      {/* Annotation toolbar */}
      {active && (
        <div className="absolute left-2 top-2 z-10 flex flex-col gap-2 rounded-xl border border-slate-700 bg-black/80 p-2 backdrop-blur-sm">
          {/* Kind buttons */}
          <div className="flex flex-col gap-1">
            {(Object.keys(kindIcons) as AnnotationKind[]).map((k) => (
              <button
                key={k}
                onClick={() => setKind(k)}
                title={k}
                className={`rounded-lg p-1.5 transition-colors ${
                  kind === k
                    ? 'bg-indigo-500 text-white'
                    : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                }`}
              >
                {kindIcons[k]}
              </button>
            ))}
          </div>

          {/* Color swatches */}
          <div className="mt-1 flex flex-col gap-1">
            {PRESET_COLORS.map((c) => (
              <button
                key={c}
                onClick={() => setColor(c)}
                style={{ backgroundColor: c }}
                className={`h-5 w-5 rounded-full ring-offset-1 transition-all ${
                  color === c ? 'ring-2 ring-white ring-offset-black' : ''
                }`}
              />
            ))}
          </div>

          {/* Clear all */}
          {annotations.length > 0 && (
            <button
              onClick={() => onChange([])}
              title={t('dental.imaging.lightbox.delete')}
              className="mt-1 rounded-lg p-1.5 text-red-400 hover:bg-red-900/30 hover:text-red-300"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      {/* Toggle button */}
      <button
        onClick={onToggleActive}
        title={t('dental.imaging.lightbox.saveAnnotations')}
        className={`rounded-lg p-2 transition-colors ${
          active
            ? 'bg-indigo-500 text-white'
            : 'bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white'
        }`}
      >
        <Pencil className="h-4 w-4" />
      </button>
    </>
  );
}
